/* eslint-disable react/no-array-index-key */
import React, {useContext, useEffect, useMemo, useRef, useState} from "react";
import PropTypes from "prop-types";
import styled, {css} from "styled-components";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faEdit} from "@fortawesome/free-solid-svg-icons";
import dayjs from "dayjs";

// Contexts
import {AuthContext} from "../../contexts/auth.js";
import {SettingsContext} from "../../contexts/settings.js";

// Hooks
import useMountedState from "../../hooks/useMountedState.js";
import useApi from "../../hooks/useApi.js";

// Utils
import {formatPhone, prettyDateInUserTimezone} from "../../utils/helpers.js";

// Components
import Modal from "../../components/Modal.js";
import ModalFileView from "../facility/ModalFileView.js";
import ModalRowExpiration from "./ModalRowExpiration.js";

// Style
import {pad} from "../../style/components/variables.js";
import {voice} from "../../style/components/typography.js";
import {Button, Heading, Inline, ModalButton, Text, Bullet} from "../../style/components/general.js";

const ModalRowPreview = ({
  visible,
  setVisible,
  selected,
  setSelected,
  hasBackButton = false,
  goBack,
  updateData,
  onEdit
}) => {
  const isMounted = useMountedState();
  const skipClearOnUnmount = useRef(false);

  const {isRole, currentUser, roleCanAccessResource} = useContext(AuthContext);

  const {settings} = useContext(SettingsContext);
  const {timezone} = settings;

  const {api} = useApi("custom-table-rows");

  const [row, setRow] = useState(selected);

  const {
    name,
    date,
    expiration,
    message,
    files: rowFiles,
    user: createdBy,
    createdAt,
    updatedBy,
    updatedAt
  } = row;

  const files = rowFiles;

  const [fileViewVisible, setFileViewVisible] = useState(!message && rowFiles?.length === 1);
  const [showModalDate, setShowModalDate] = useState(false);
  const [selectedFile, setSelectedFile] = useState(
    !message && rowFiles?.length === 1 ? rowFiles[0] : null
  );

  useEffect(
    () => () => {
      if (!isMounted() && !skipClearOnUnmount.current && setSelected) setSelected(null);
    },
    [isMounted, setSelected]
  );

  const replacePhone = p => p.replace("tel=", "").replace(";", "");
  const replaceEmail = m => m.replace("mail=", "").replace(";", "");
  const replaceUrl = m => m.replace("url=", "").replace(";", "");
  const replaceName = m => m.replace("name=", "").replace(";", "");

  const formattedNodes = useMemo(() => {
    if (!message) return [];

    // First, split the message by lines
    return message.split("\n").flatMap((line, i) => {
      const parts = [];
      let remaining = line;
      const pattern =
        /(tel=\d{10};(?:name=[^;]+;)?|mail=[^;]+;(?:name=[^;]+;)?|url=[^;]+;(?:name=[^;]+;)?)/;
      let match;

      // eslint-disable-next-line no-cond-assign
      while ((match = remaining.match(pattern))) {
        const [full] = match;
        const idx = remaining.indexOf(full);

        if (idx > 0) parts.push(remaining.slice(0, idx));

        if (full.startsWith("tel=")) {
          const num = replacePhone(full);
          const nameMatch = full.match(/name=([^;]+);?/);
          parts.push(
            <a key={`${i}-${parts.length}`} href={`tel:${num}`}>
              {nameMatch ? replaceName(nameMatch[0]) : formatPhone(num)}
            </a>
          );
        } else if (full.startsWith("mail=")) {
          const email = replaceEmail(full);
          const nameMatch = full.match(/name=([^;]+);?/);
          parts.push(
            <a key={`${i}-${parts.length}`} href={`mailto:${email}`}>
              {nameMatch ? replaceName(nameMatch[0]) : email}
            </a>
          );
        } else if (full.startsWith("url=")) {
          const url = replaceUrl(full);
          const nameMatch = full.match(/name=([^;]+);?/);
          parts.push(
            <a key={`${i}-${parts.length}`} href={url} target="_blank" rel="noopener noreferrer">
              {nameMatch ? replaceName(nameMatch[0]) : url}
            </a>
          );
        }

        remaining = remaining.slice(idx + full.length);
      }

      if (remaining) parts.push(remaining);

      return i < message.split("\n").length - 1 ? [...parts, <br key={`br-${i}`} />] : parts;
    });
  }, [message]);

  const updateExpiration = ({expiration: selectedExpiration}) => {
    api
      .callPut(
        selected.id,
        {},
        {params: {expiration: selectedExpiration, userId: currentUser.publicId}}
      )
      .then(({status, data}) => {
        if (status === 200) {
          setShowModalDate(false);
          setRow(data.data);
          if (updateData) updateData();
        }
      });
  };

  const expirationFormatted = useMemo(
    () => prettyDateInUserTimezone(expiration, timezone, "MMM D YYYY"),
    [expiration, timezone]
  );

  const canEdit = onEdit && roleCanAccessResource("custom_table_row", "update");

  const handleEdit = () => {
    skipClearOnUnmount.current = true;
    onEdit(row);
  };

  if (showModalDate)
    return (
      <ModalRowExpiration
        visible={showModalDate}
        setVisible={setVisible}
        goBack={() => setShowModalDate(false)}
        update={updateExpiration}
        expiration={expiration || undefined}
      />
    );

  if (files && fileViewVisible)
    return (
      <ModalFileView
        visible={fileViewVisible}
        setVisible={setVisible}
        file={selectedFile}
        hasBackButton={!!message || files.length > 1 || hasBackButton}
        goBack={
          !!message || files.length > 1
            ? () => {
                setSelectedFile(null);
                setFileViewVisible(false);
              }
            : goBack
        }
        updateExpiration={
          !message && files?.length === 1 ? () => setShowModalDate(true) : undefined
        }
        expiration={!message && files?.length === 1 ? expiration : undefined}
        date={!message && files?.length === 1 ? date : undefined}
      />
    );

  return (
    <Modal
      visible={visible}
      setVisible={setVisible}
      hasBackButton={hasBackButton}
      goBack={goBack}
      renderModalControls={
        canEdit || !expiration ? (
          <>
            {canEdit && (
              <ModalButton type="button" onClick={handleEdit} title="Edit">
                <FontAwesomeIcon icon={faEdit} />
              </ModalButton>
            )}
            {!expiration && (
              <Addition type="button" onClick={() => setShowModalDate(true)} quiet>
                SET EXPIRATION DATE
              </Addition>
            )}
          </>
        ) : undefined
      }>
      {!isRole("Lab") && <Heading>{name}</Heading>}
      {date && (
        <Date>
          <strong>ORIGINAL DATE:</strong>&nbsp;
          <NoBreak>{prettyDateInUserTimezone(date, timezone, "MMM D YYYY")}</NoBreak>
        </Date>
      )}
      <Date>
        <strong>CREATED:</strong> <NoBreak>{prettyDateInUserTimezone(createdAt, timezone)}</NoBreak>
        &nbsp; by <NoBreak>{createdBy}</NoBreak>
      </Date>
      {updatedAt && updatedBy && (
        <Date>
          <strong>UPDATED:</strong>&nbsp;
          <NoBreak>{prettyDateInUserTimezone(updatedAt, timezone)}</NoBreak> by&nbsp;
          <NoBreak>{updatedBy}</NoBreak>
        </Date>
      )}
      {expirationFormatted && (
        <Inline>
          <Date error={dayjs(expirationFormatted).isBefore(dayjs.utc().tz(timezone), "day")}>
            <strong>EXPIRATION:</strong>&nbsp;
            <NoBreak>{expirationFormatted}</NoBreak>
          </Date>
          <Edit type="button" onClick={() => setShowModalDate(true)}>
            <FontAwesomeIcon icon={faEdit} />
          </Edit>
        </Inline>
      )}
      <br />
      <NoteMessage>{formattedNodes}</NoteMessage>
      {files?.length > 0 && (
        <Files>
          <hr />
          <Attached>Attached File(s)</Attached>
          {files.map(file => (
            <Bullet key={`file-${file.id}`}>
              <FileLink
                type="button"
                onClick={() => {
                  setSelectedFile(file);
                  setFileViewVisible(true);
                }}>
                {file.label}
              </FileLink>
            </Bullet>
          ))}
        </Files>
      )}
    </Modal>
  );
};

ModalRowPreview.propTypes = {
  visible: PropTypes.bool.isRequired,
  setVisible: PropTypes.func.isRequired,
  selected: PropTypes.objectOf(PropTypes.any),
  setSelected: PropTypes.func,
  hasBackButton: PropTypes.bool,
  goBack: PropTypes.func,
  updateData: PropTypes.func,
  onEdit: PropTypes.func
};

// Style Overrides
const NoteMessage = styled(Text)`
  a {
    display: inline;
    color: ${({theme}) => theme.primary};
    font-weight: bold;
  }
`;

const Files = styled.div`
  text-align: left;
  color: ${({theme}) => theme.secondary};
  margin-bottom: ${pad / 2}px;
  ${voice.quiet};

  p {
    font-weight: bold;
  }
`;

const FileLink = styled.button`
  ${voice.quiet};
  font-weight: bold;
  color: ${({theme}) => theme.secondary};
  text-decoration: underline;
  width: fit-content;
  cursor: pointer;
  background: transparent;
  border: none;
  padding: 0;
`;

const Attached = styled(Text)`
  margin-bottom: ${pad / 3}px;
`;

const Date = styled(Text)`
  ${voice.weak};
  margin-bottom: ${pad / 3}px;

  ${({error, theme}) =>
    error &&
    css`
      color: ${theme.error};
    `}
`;

const NoBreak = styled.span`
  white-space: nowrap;
`;

const Addition = styled(Button)`
  ${voice.quiet};
  padding: ${pad / 2}px;
  margin-left: ${pad}px;
`;

const Edit = styled.button`
  width: fit-content;
  margin: 5px 0 0 -5px;

  svg {
    fill: ${({theme}) => theme.secondary};
    padding: ${pad / 2}px 0 ${pad}px ${pad}px;
    ${voice.quiet}
  }
`;

export default ModalRowPreview;
